/**
 * SimOps slice — draft study configuration state for the setup form.
 */
import type { StateCreator } from 'zustand';
import type {
  StudyConfig,
  StudyKind,
  InternalFlowConfig,
  SolverConfig,
  SolverSettings,
  ChtSteadyParams,
  SimulationCategory,
  MeshSettings,
  SelectedInputs,
  PatchBC,
  ManualDesignPoint,
  ManualDpInputOption,
} from '../../routes/_app/simops/-data/types';
import type {
  StructuralStaticParams,
  ModalFrequencyParams,
  LinearBucklingParams,
  ThermalConductionParams,
  ThermalTransientParams,
} from '../../api/generated/types.gen';
import type {
  CfdFlowConfig,
  CfdFlowConfigBase,
  Template,
  MeshMode,
} from '../../routes/_app/simops/-data/types';
import {
  AutoRetryConfig,
  DEFAULT_INTERNAL_FLOW_CONFIG,
  DEFAULT_SOLVER_CONFIG,
  DEFAULT_MESH_SETTINGS,
  DEFAULT_CHT_PARAMS,
  DEFAULT_SOBOL,
  DEFAULT_OPTIMIZATION,
  PLACEHOLDER_FILENAME_MARKER,
  TEMPLATE_REGISTRY,
  asCfdFlow,
  isCfdFlowSolver,
  isInternalFlowSolver,
  asExternalFlow,
  categoryOf,
  defaultDraftFor,
  isRange,
} from '../../routes/_app/simops/-data/types';
import { SURROGATE_MODEL_AVAILABLE } from '../../routes/_app/simops/-data/feature-flags';
import { STRUCTURAL_MATERIALS } from '@/constants/structuralMaterials.gen';
import { THERMAL_MATERIALS } from '@/constants/thermalMaterials.gen';
import { pruneThermalParameterSweeps } from '../../routes/_app/simops/-data/thermalSweepAxes';
import type {
  Objective,
  OptimizationExtension,
  RangeSpec,
  Sobol,
  StudyDetailResponse,
} from '@/api/generated/types.gen';
import type { StoreState } from '../useStore';
import { sameAutoRetryConfig } from '@/utils/meshRetry';
import { logger } from '@/lib/logger';

// For fluid props (rho/mu/nu), the SimOps form only models discrete arrays —
// the rho ↔ mu ↔ nu derivation requires arrays — so any persisted RangeSpec
// is dropped on load. Inlet/outlet keep RangeSpec under the surrogate path.
const asDiscrete = <T>(v: T[] | RangeSpec | undefined): T[] | undefined =>
  Array.isArray(v) ? v : undefined;

/** True when ``material`` is a key of the library the template's draft uses. */
function materialKnownForTemplate(
  template: Template,
  material: string,
): boolean {
  if (template === 'thermal_conduction' || template === 'thermal_transient') {
    return material in THERMAL_MATERIALS;
  }
  if (
    template === 'structural_static' ||
    template === 'modal_frequency' ||
    template === 'linear_buckling'
  ) {
    return material in STRUCTURAL_MATERIALS;
  }
  return false;
}

// Both sides are the server's `updated_at`, so no client clock is involved.
// An unparseable or missing stamp is never newer, which keeps the store.
const isNewerStamp = (
  candidate: string | null | undefined,
  held: string | null,
): boolean =>
  candidate != null &&
  (held == null || Date.parse(candidate) > Date.parse(held));

type DraftInitResult = 'loaded' | 'reloaded' | 'kept';

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

/** The draft's setup steps, as `DraftSetup`'s step tree names them. */
export const DRAFT_FOCUS_STEPS = ['physics', 'study', 'advanced'] as const;
export type DraftFocusStep = (typeof DRAFT_FOCUS_STEPS)[number];

export interface SimopsSlice {
  draftStudyId: string | null;
  /**
   * The study's `updated_at` for the config the store holds: set when
   * `initDraftConfig` loads it and advanced by `markDraftSaved` after each
   * save. A study whose `updated_at` is newer was written from outside the
   * page (a chat MCP tool, another client), so the store is stale (ENG-2655).
   */
  draftLoadedAt: string | null;
  draftConfig: StudyConfig;
  activeDraftInputId: string | null;
  /**
   * A request to open one of the draft's setup steps (ENG-2640), made by the
   * assistant's `set_simops_view`. The step tree opens the step when `key`
   * changes, so asking for the same step again opens it again after the
   * reader closed it. The field inside is found and focused by the tool once
   * the step's content has mounted, so the store holds only the step.
   */
  draftFocus: { step: DraftFocusStep; key: number } | null;
  lastSavedAt: Date | null;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';

  /**
   * Load a study's config into the draft store. `'loaded'` on a new study,
   * `'reloaded'` when the same study's database copy is newer than the store,
   * `'kept'` when the store is already current and nothing changed.
   */
  initDraftConfig: (study: StudyDetailResponse) => DraftInitResult;
  /** Record the `updated_at` a save of `studyId` came back with, so the
   *  page's own write does not read as an out-of-band one. */
  markDraftSaved: (studyId: string, updatedAt: string) => void;
  setSaveStatus: (status: SimopsSlice['saveStatus'], savedAt?: Date) => void;
  setSelectedInputs: (inputs: SelectedInputs | null) => void;
  setActiveDraftInputId: (id: string | null) => void;
  focusDraftStep: (step: DraftFocusStep) => void;
  /**
   * Swap `solver_settings` to the first template registered in the given
   * UI category. Named distinctly from `toolSlice.setSimulationType`
   * (which sets MeshGen's mesh-params simulationType) so the unified
   * store keeps them separable rather than letting the latter spread
   * mask this one. The store no longer carries `simulation_type` as a
   * field — the category is derived from `solver_settings.template`.
   */
  setStudySimulationType: (type: SimulationCategory) => void;
  /**
   * Patch the discriminated `solver_settings` blob. Caller passes a partial
   * of either `SolverConfig` (CFD) or `ChtSteadyParams` (CHT); the active
   * template determines which shape is in play. Swapping templates is the
   * responsibility of `setStudySimulationType`.
   */
  patchSolverSettings: (
    patch:
      | Partial<SolverConfig>
      | Partial<InternalFlowConfig>
      | Partial<ChtSteadyParams>
      | Partial<StructuralStaticParams>
      | Partial<ModalFrequencyParams>
      | Partial<LinearBucklingParams>
      | Partial<ThermalConductionParams>
      | Partial<ThermalTransientParams>,
  ) => void;
  setBoundaryConditions: (bcs: PatchBC[]) => void;
  /**
   * Patch SimOps's local mesh settings (`draftConfig.mesh_settings`). Named
   * distinctly from `toolSlice.setMeshSettings` (which patches MeshGen's
   * global mesh params) to avoid the unified-store key collision.
   */
  patchMeshSettings: (patch: Partial<MeshSettings>) => void;
  /** Set the study method. Pair with the badge / picker PATCH so the draft
   *  store stays in sync — the autosave path reads from here. */
  setStudyKind: (kind: StudyKind) => void;
  /** Toggle surrogate-modeling mode for the draft study. */
  setSurrogateEnabled: (enabled: boolean) => void;
  /** Set or clear the study-wide automatic AI retry setting. */
  setAutoRetry: (settings: AutoRetryConfig | null) => void;
  setDraftMeshAutoRetry: (settings: AutoRetryConfig | null) => void;
  /** Patch the Sobol sampling config; only relevant when surrogate is on. */
  patchSampling: (patch: Partial<Sobol>) => void;
  /**
   * Patch the optimization (MOBO) config. Pass `null` to disable optimization
   * entirely; pass a partial to update fields on the existing extension. The
   * slice promotes a partial to a full extension by merging with
   * `DEFAULT_OPTIMIZATION` when called for the first time.
   */
  patchOptimization: (patch: Partial<OptimizationExtension> | null) => void;
  /** Append a new objective to the optimization extension. */
  addObjective: (objective: Objective) => void;
  /** Remove the objective at `index`. */
  removeObjective: (index: number) => void;
  /** Patch the objective at `index`. */
  updateObjective: (index: number, patch: Partial<Objective>) => void;
  /**
   * Swap `solver_settings` to the given FEA template, preserving the
   * current material across the swap. No-op if the template is already
   * active. Resets BCs and n_modes to the new template's defaults (load BCs
   * are invalid for modal; resetting is correct).
   */
  setStudyTemplate: (template: Template) => void;
  /**
   * Correct `solver_settings` to the template the picked mesh implies,
   * KEEPING every field the two CFD flow templates share (ENG-2444).
   *
   * Distinct from `setStudyTemplate`, which discards the old settings —
   * correct for a user deliberately switching template, destructive here.
   * This fires automatically, so it can land after the draft has been
   * edited; dropping the settings then silently threw away the user's (or
   * the copilot's) work, which is what the simops-copilot e2e caught.
   *
   * CFD-only: the shared shape is `CfdFlowConfigBase`, and no other pair of
   * templates has one. No-op unless both sides are CFD flow templates.
   */
  applyMeshDerivedTemplate: (template: Template, meshMode?: MeshMode) => void;
  /**
   * Replace the per-geometry mesh picks for the mesh-in-draft flow. Each
   * entry is a mesh id the user explicitly chose; overlaid on top of the
   * latest-completed default at render time.
   */
  setMeshPicks: (ids: string[]) => void;
  /**
   * Append (or replace by `id`) a manual design point in the draft. Same
   * merge semantics as the BE POST /design-points/manual endpoint — an
   * incoming entry with an existing `id` replaces that row; a new `id`
   * appends. Autosave PATCH picks the whole list up on the next tick.
   */
  upsertManualDesignPoint: (dp: ManualDesignPoint) => void;
  removeManualDesignPoint: (id: string) => void;
  /**
   * Drop any manual DPs whose `input_id` is no longer a valid matrix-input id.
   * Called from `DraftSetup` when the mesh-pick / geometry-selection set
   * changes so an orphaned manual DP (e.g. its picked mesh got deselected)
   * can't reach `run_study` and produce the "input_id not in inputs.*_ids"
   * 400 the user has no way to fix from the UI. Returns the dropped ids so
   * the caller can surface a toast.
   */
  pruneStaleManualDesignPoints: (validInputIds: Iterable<string>) => string[];
  /**
   * Manual DP id the user is currently editing. When set, StudyPreview
   * opens the DP editor + the header banner + SolverSettingsForm's
   * sweep-field hints all key off this. Cleared on Done, on nav, or
   * when the DP is removed. Null = "editing study defaults" (the
   * normal draft-authoring mode).
   */
  editingManualDpId: string | null;
  setEditingManualDpId: (id: string | null) => void;

  /**
   * The inputs the Add-manual-DP popover offers for the draft on screen, and
   * which study they belong to. Derived by `DraftSetup` from the mesh queries
   * and mirrored here so `update_simops_draft` adds a point on exactly the
   * inputs the reader could pick (ENG-2639). View state: never saved.
   */
  manualDpInputOptions: {
    studyId: string;
    options: ManualDpInputOption[];
  } | null;
  setManualDpInputOptions: (
    value: { studyId: string; options: ManualDpInputOption[] } | null,
  ) => void;

  /**
   * Manual DPs currently multi-selected in the draft study preview.
   * Lifted from StudyPreview local state so top-bar chip, sidebar
   * banner, and preview toolbar can all reflect the selection in step
   * (orbiting outline + "N DP selected" copy). Cleared when the
   * selection is deleted or explicitly cleared.
   */
  selectedManualDpIds: Set<string>;
  setSelectedManualDpIds: (ids: Iterable<string>) => void;
  clearSelectedManualDpIds: () => void;
  toggleSelectedManualDpId: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const DEFAULT_CONFIG: StudyConfig = {
  kind: 'doe',
  selected_inputs: null,
  meshPicks: [],
  mesh_settings: DEFAULT_MESH_SETTINGS,
  solver_settings: DEFAULT_SOLVER_CONFIG,
  boundary_conditions: [],
  surrogate_enabled: false,
  auto_retry: null,
  mesh_auto_retry: null,
  sampling: null,
  optimization: null,
  extra_design_points: [],
};

/**
 * Apply the surrogate-enabled invariant to a draft config (ENG-3135).
 *
 * On enable: default the Sobol sampling and collapse any multi-value discrete
 * sweep list to its first value (surrogate mode allows only Single or Range per
 * field). On disable: drop sampling + optimization and turn any RangeSpec back
 * into a `[lower]` discrete list, so the discrete editor — and a runnable
 * non-surrogate study — stay valid. Only CFD flow leaves carry the sweepable
 * `inlet.velocity` / `outlet.pressure`; CHT/FEA solvers are left untouched.
 */
function withSurrogateEnabled(
  draft: StudyConfig,
  enabled: boolean,
): StudyConfig {
  const reshape = (v: number[] | RangeSpec): number[] | RangeSpec => {
    if (!enabled && isRange(v)) return [v.lower];
    if (enabled && Array.isArray(v) && v.length > 1) return [v[0]];
    return v;
  };
  const solver = draft.solver_settings;
  const nextSolver: SolverSettings = isCfdFlowSolver(solver)
    ? (() => {
        const nextVelocity = solver.inlet
          ? reshape(solver.inlet.velocity)
          : null;
        const nextPressure = solver.outlet
          ? reshape(solver.outlet.pressure)
          : null;
        const inletChanged =
          solver.inlet && nextVelocity !== solver.inlet.velocity;
        const outletChanged =
          solver.outlet && nextPressure !== solver.outlet.pressure;
        if (!inletChanged && !outletChanged) return solver;
        const nextInlet = inletChanged
          ? { velocity: nextVelocity as number[] | RangeSpec }
          : solver.inlet;
        const nextOutlet = outletChanged
          ? { pressure: nextPressure as number[] | RangeSpec }
          : solver.outlet;
        return isInternalFlowSolver(solver)
          ? { ...solver, inlet: nextInlet ?? solver.inlet, outlet: nextOutlet }
          : { ...solver, inlet: nextInlet, outlet: nextOutlet };
      })()
    : solver;
  return {
    ...draft,
    surrogate_enabled: enabled,
    sampling: enabled ? (draft.sampling ?? DEFAULT_SOBOL) : null,
    // Optimization is a strict superset of Sobol — disabling drops it;
    // enabling does not auto-enable MOBO.
    optimization: enabled ? draft.optimization : null,
    solver_settings: nextSolver,
  };
}

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createSimopsSlice: StateCreator<
  StoreState,
  [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]],
  [],
  SimopsSlice
> = (set, get) => ({
  draftStudyId: null,
  draftLoadedAt: null,
  draftConfig: DEFAULT_CONFIG,
  activeDraftInputId: null,
  draftFocus: null,
  lastSavedAt: null,
  saveStatus: 'idle',

  setSaveStatus: (status, savedAt) =>
    set({ saveStatus: status, ...(savedAt ? { lastSavedAt: savedAt } : {}) }),

  initDraftConfig: (study) => {
    const prev = get();
    const sameStudy = prev.draftStudyId === study.id;
    if (sameStudy && !isNewerStamp(study.updated_at, prev.draftLoadedAt)) {
      return 'kept';
    }

    const raw: Partial<NonNullable<StudyDetailResponse['config']>> =
      study.config ?? {};
    // Shared CFD fields (nu, inlet, outlet, turbulence) read off EITHER flow
    // template — the two leaves carry them identically. `of` stays external-only
    // below for `force_coeffs`, which the internal leaf does not have.
    const cfd = asCfdFlow(raw.solver_settings);
    const of = asExternalFlow(raw.solver_settings);
    // Detect a CHT-saved study by the discriminator on solver_settings — the
    // backend persists ChtSteadyParams there. Rehydrating into the CHT path
    // preserves the user's per-face BCs and material choice on next open.
    const persistedSolver = raw.solver_settings as
      | { template?: string }
      | undefined;
    const isCht = persistedSolver?.template === 'cht_steady';

    // The backend only persists `nu`; rho/mu default to the Air preset on load,
    // and `nu` remains the derived field so the stored value is preserved
    // until the user edits rho or mu explicitly.
    const nu = asDiscrete(cfd?.nu) ?? DEFAULT_SOLVER_CONFIG.nu;
    const rho = DEFAULT_SOLVER_CONFIG.rho;
    const mu = nu.map((v, i) => v * (rho[Math.min(i, rho.length - 1)] ?? 1));
    // Inlet velocity and outlet pressure may be a discrete list OR a
    // RangeSpec when surrogate modeling is on; keep both shapes.
    const rawVelocity = cfd?.inlet?.velocity;
    const velocity = isRange(rawVelocity)
      ? rawVelocity
      : asDiscrete(rawVelocity);
    // Persisted inlet direction (ENG-1015).
    const inletDirection = cfd?.inlet?.direction ?? null;
    const rawPressure = cfd?.outlet?.pressure;
    const pressure = isRange(rawPressure)
      ? rawPressure
      : asDiscrete(rawPressure);
    const sampling = (raw.sampling ?? null) as Sobol | null;
    const optimization = (raw.optimization ??
      null) as OptimizationExtension | null;

    // Rehydrate selected_inputs from whichever input list the backend
    // persisted. Filenames carry the placeholder marker so
    // `useHydrateSelectedInputs` knows to follow up with a real fetch.
    const geoIds = raw.inputs?.geometry_ids ?? [];
    const meshIds = raw.inputs?.mesh_ids ?? [];
    const placeholder = (id: string) =>
      id.slice(0, 8) + PLACEHOLDER_FILENAME_MARKER;
    let selectedInputs: SelectedInputs | null = null;
    if (geoIds.length > 0) {
      selectedInputs = {
        kind: 'geometry',
        items: geoIds.map((id) => ({ id, filename: placeholder(id) })),
      };
    } else if (meshIds.length > 0) {
      selectedInputs = {
        kind: 'mesh',
        items: meshIds.map((id) => ({
          id,
          filename: placeholder(id),
          geometry_id: '',
        })),
      };
    }

    // Choose the rehydrated solver_settings shape by discriminator:
    //   - cht_steady: merge over defaults so partial saves stay valid.
    //   - external_flow: rebuild the FE-augmented SolverConfig (rho/mu
    //     are FE-only; nu is the persisted source of truth).
    //   - internal_flow: same rebuild, minus force_coeffs (which that
    //     backend model does not carry) and plus flow_opening_roles.
    //   - any other backend template (structural_static, modal_frequency,
    //     thermal_conduction, urans): preserve the persisted wire shape
    //     verbatim. SolverSettingsForm renders the unsupported-template
    //     panel until those forms ship; the next autosave round-trips the
    //     original blob unchanged.
    //   - missing/unknown: fall back to the CFD external_flow default.
    const persistedTemplate = persistedSolver?.template;
    const solverSettings: SolverSettings = isCht
      ? {
          ...DEFAULT_CHT_PARAMS,
          ...(raw.solver_settings as unknown as ChtSteadyParams),
        }
      : of
        ? {
            template: 'external_flow',
            flow_type: of.flow_type ?? DEFAULT_SOLVER_CONFIG.flow_type,
            rho,
            mu,
            nu,
            fluid_derived: DEFAULT_SOLVER_CONFIG.fluid_derived,
            flow_axis: of.flow_axis ?? DEFAULT_SOLVER_CONFIG.flow_axis,
            inlet: velocity
              ? {
                  velocity,
                  ...(inletDirection ? { direction: inletDirection } : {}),
                }
              : DEFAULT_SOLVER_CONFIG.inlet,
            outlet: pressure ? { pressure } : DEFAULT_SOLVER_CONFIG.outlet,
            overrides: of.overrides ?? {},
            turbulence_intensity:
              of.turbulence_intensity ??
              DEFAULT_SOLVER_CONFIG.turbulence_intensity,
            turbulent_length_scale:
              of.turbulent_length_scale ??
              DEFAULT_SOLVER_CONFIG.turbulent_length_scale,
            force_coeffs: of.force_coeffs ? { ...of.force_coeffs } : null,
          }
        : cfd
          ? {
              // internal_flow. Same shared fields as the arm above; `inlet` is
              // required here, so it falls back to the default rather than to
              // null, and `flow_opening_roles` carries the patch assignment.
              template: 'internal_flow',
              flow_type: cfd.flow_type ?? DEFAULT_SOLVER_CONFIG.flow_type,
              rho,
              mu,
              nu,
              fluid_derived: DEFAULT_SOLVER_CONFIG.fluid_derived,
              flow_axis: cfd.flow_axis ?? DEFAULT_SOLVER_CONFIG.flow_axis,
              inlet: velocity
                ? {
                    velocity,
                    ...(inletDirection ? { direction: inletDirection } : {}),
                  }
                : DEFAULT_INTERNAL_FLOW_CONFIG.inlet,
              outlet: pressure
                ? { pressure }
                : DEFAULT_INTERNAL_FLOW_CONFIG.outlet,
              overrides: cfd.overrides ?? {},
              turbulence_intensity:
                cfd.turbulence_intensity ??
                DEFAULT_SOLVER_CONFIG.turbulence_intensity,
              turbulent_length_scale:
                cfd.turbulent_length_scale ??
                DEFAULT_SOLVER_CONFIG.turbulent_length_scale,
              flow_opening_roles:
                (
                  raw.solver_settings as unknown as {
                    flow_opening_roles?: Record<string, 'inlet' | 'outlet'>;
                  }
                )?.flow_opening_roles ?? {},
            }
          : persistedTemplate && persistedTemplate in TEMPLATE_REGISTRY
            ? // Persisted template is a registered backend template. Pass the
              // wire shape through verbatim on reload. The `as unknown as
              // SolverSettings` double-cast is
              // needed because `SolverSettings` substitutes the FE-augmented
              // `SolverConfig` for the raw `external_flow` member, so
              // `RawSolverSettings` is structurally wider than `SolverSettings`
              // and TS can't prove the assignment via a single cast.
              (raw.solver_settings as unknown as SolverSettings)
            : DEFAULT_SOLVER_CONFIG;

    const persistedKind: StudyKind =
      raw.kind === 'surrogate' || raw.kind === 'gci' ? raw.kind : 'doe';

    // A surrogate study is surrogate-enabled by its kind (ENG-3135). Driving
    // this off the kind — not off leftover sampling/range artifacts — keeps a
    // study switched to DOE/GCI from resurrecting the surrogate surface on
    // reload. The feature flag still gates the whole surface off.
    const persistedSurrogate =
      SURROGATE_MODEL_AVAILABLE && persistedKind === 'surrogate';

    // rho, mu and fluid_derived are FE-only: the wire carries nu alone, so the
    // rebuild above resets them to the Air preset. A reload that leaves nu as
    // it was keeps the fluid the user picked this session.
    const prevSolver = prev.draftConfig.solver_settings;
    const draftSolver: SolverSettings =
      sameStudy &&
      isCfdFlowSolver(solverSettings) &&
      isCfdFlowSolver(prevSolver) &&
      prevSolver.template === solverSettings.template &&
      prevSolver.nu.length === nu.length &&
      prevSolver.nu.every((v, i) => v === nu[i])
        ? {
            ...solverSettings,
            rho: prevSolver.rho,
            mu: prevSolver.mu,
            fluid_derived: prevSolver.fluid_derived,
          }
        : solverSettings;

    // Ids the reload keeps pointing at; anything else went with the old copy.
    const manualIds = new Set(
      (raw.extra_design_points ?? []).map((dp) => dp.id),
    );
    const inputIds = new Set(selectedInputs?.items.map((item) => item.id));

    set({
      draftStudyId: study.id,
      draftLoadedAt: study.updated_at ?? null,
      draftConfig: {
        kind: persistedKind,
        selected_inputs: selectedInputs,
        // Reload mesh picks from the persisted mesh_ids when the study is
        // geometry-kind (geoIds present). On reload the component resolves
        // picks back against each geometry's mesh list. Non-geometry studies
        // (pure mesh-in) carry no picks — their selection is the mesh list.
        meshPicks: geoIds.length > 0 ? (raw.inputs?.mesh_ids ?? []) : [],
        // Never persisted, so the database copy has nothing to say about it:
        // a reload of the same study keeps what the user set this session.
        mesh_settings: sameStudy
          ? prev.draftConfig.mesh_settings
          : DEFAULT_MESH_SETTINGS,
        solver_settings: draftSolver,
        boundary_conditions:
          ((raw.solver_settings as Record<string, unknown>)
            ?.boundary_conditions as PatchBC[] | undefined) ?? [],
        surrogate_enabled: persistedSurrogate,
        sampling: persistedSurrogate ? (sampling ?? DEFAULT_SOBOL) : null,
        optimization: persistedSurrogate ? optimization : null,
        auto_retry:
          (raw.auto_retry as StudyConfig['auto_retry'] | undefined) ?? null,
        mesh_auto_retry:
          (raw.mesh_auto_retry as StudyConfig['mesh_auto_retry'] | undefined) ??
          null,
        // Manual DPs echoed by the BE verbatim (ManualDesignPoint.id is
        // FE-stable across autosave, and lands on dp.inputs.manual_id when
        // the row materializes at run time).
        extra_design_points: raw.extra_design_points ?? [],
      },
      // Everything below is view state, not config. A study switch resets it;
      // a reload of the same study keeps it, dropping only ids the new copy
      // no longer has, so the user's view survives an out-of-band write.
      ...(sameStudy
        ? {
            activeDraftInputId:
              prev.activeDraftInputId != null &&
              inputIds.has(prev.activeDraftInputId)
                ? prev.activeDraftInputId
                : (selectedInputs?.items[0]?.id ?? null),
            editingManualDpId:
              prev.editingManualDpId != null &&
              manualIds.has(prev.editingManualDpId)
                ? prev.editingManualDpId
                : null,
            selectedManualDpIds: new Set(
              [...prev.selectedManualDpIds].filter((id) => manualIds.has(id)),
            ),
          }
        : {
            activeDraftInputId: selectedInputs?.items[0]?.id ?? null,
            // Seed enclosureEnabled once per draft so new external_flow
            // drafts land on Simulate with the box visible; a user's
            // eye-toggle off survives step navigation and reloads.
            // Non-external templates hide the box.
            enclosureEnabled: solverSettings.template === 'external_flow',
            // The previous study's manual-DP ids would render a stale count
            // / edit-mode chip against the new study's DP list.
            editingManualDpId: null,
            selectedManualDpIds: new Set<string>(),
          }),
    });
    return sameStudy ? 'reloaded' : 'loaded';
  },

  markDraftSaved: (studyId, updatedAt) => {
    const { draftStudyId, draftLoadedAt } = get();
    // A late response for a study the store has moved off, or one older than
    // a reload that already landed, must not move the stamp backwards.
    if (draftStudyId !== studyId || !isNewerStamp(updatedAt, draftLoadedAt)) {
      return;
    }
    set({ draftLoadedAt: updatedAt });
  },

  setSelectedInputs: (inputs) =>
    set((s) => {
      const activeId = s.activeDraftInputId;
      const activeStillSelected =
        activeId != null && inputs?.items.some((item) => item.id === activeId);
      return {
        draftConfig: {
          ...s.draftConfig,
          selected_inputs: inputs,
        },
        activeDraftInputId: activeStillSelected
          ? activeId
          : (inputs?.items[0]?.id ?? null),
      };
    }),

  setActiveDraftInputId: (id) => set({ activeDraftInputId: id }),
  focusDraftStep: (step) =>
    set((state) => ({
      draftFocus: { step, key: (state.draftFocus?.key ?? 0) + 1 },
    })),

  setStudySimulationType: (type) =>
    set((s) => {
      const current = s.draftConfig.solver_settings.template;
      if (categoryOf(current) === type) return s;
      // Swap the solver_settings to a template in the target category that has
      // a registered FE draft. Prefer structural_static when entering FEA so
      // thermal_conduction (listed first in the registry) does not become the
      // default category pick.
      const drafted = (Object.keys(TEMPLATE_REGISTRY) as Template[]).filter(
        (t) => categoryOf(t) === type && defaultDraftFor(t) != null,
      );
      const firstTemplate =
        type === 'fea' && drafted.includes('structural_static')
          ? 'structural_static'
          : drafted[0];
      const nextDraft = firstTemplate ? defaultDraftFor(firstTemplate) : null;
      if (!nextDraft) {
        // No template in this category has a registered FE draft. The sim-type
        // toggle only exposes categories that have a drafted template, so a
        // user-driven call should never reach here — a programmatic caller
        // bypassed that gate or the registry was misconfigured.
        logger.error(
          `setStudySimulationType: no draft registered for category "${type}"; ignoring`,
        );
        return s;
      }
      return {
        draftConfig: {
          ...s.draftConfig,
          solver_settings: nextDraft,
        },
      };
    }),

  setStudyTemplate: (template) =>
    set((s) => {
      const current = s.draftConfig.solver_settings;
      if (current.template === template) return s;
      const draft = defaultDraftFor(template);
      if (!draft) {
        logger.error(
          `setStudyTemplate: no draft registered for "${template}"; ignoring`,
        );
        return s;
      }
      // Carry `material` only when it exists in the destination template's
      // library. Structural and thermal FEA share the FEA toggle but not the
      // same keys (e.g. `peek` vs `diamond_cvd`); copying blindly left the
      // picker empty and autosave persisted a value `_known_material` rejects
      // (ENG-2619).
      const material = (current as { material?: string }).material;
      const next =
        material && materialKnownForTemplate(template, material)
          ? { ...draft, material }
          : { ...draft };
      // A buckling load pattern is authored from the same FEA face sets as a
      // static run. Carry only the BC types this eigensolve actually accepts;
      // dropping an unsupported contact or body load is explicit here rather
      // than persisting a draft that the backend cannot run.
      if (
        template === 'linear_buckling' &&
        (current.template === 'structural_static' ||
          current.template === 'modal_frequency')
      ) {
        const allowed = new Set([
          'fixed_face',
          'fixed_body',
          'restraint',
          'load_face',
          'load_point',
          'pressure',
        ]);
        (next as LinearBucklingParams).boundary_conditions = (
          current.boundary_conditions ?? []
        ).filter((bc) =>
          allowed.has(bc.type),
        ) as LinearBucklingParams['boundary_conditions'];
      }
      // Steady ↔ transient thermal share BC / tie / material surfaces
      // (ENG-3002 / TH-6). Leave time schedule and IC on the destination
      // draft defaults — carrying a 60 s period onto steady would be noise,
      // and dropping IC when leaving transient is intentional. Prune sweeps
      // so an `initial_temperature_K` axis does not survive onto conduction.
      if (
        (template === 'thermal_conduction' ||
          template === 'thermal_transient') &&
        (current.template === 'thermal_conduction' ||
          current.template === 'thermal_transient')
      ) {
        const src = current as {
          boundary_conditions?: ThermalConductionParams['boundary_conditions'];
          tied_interfaces?: ThermalConductionParams['tied_interfaces'];
          default_convection_h?: number;
          materials_by_volume?: ThermalConductionParams['materials_by_volume'];
          parameter_sweeps?: ThermalConductionParams['parameter_sweeps'];
        };
        const thermalNext = {
          ...(next as ThermalConductionParams | ThermalTransientParams),
          boundary_conditions: src.boundary_conditions,
          tied_interfaces: src.tied_interfaces,
          default_convection_h: src.default_convection_h,
          materials_by_volume: src.materials_by_volume,
          parameter_sweeps: src.parameter_sweeps,
        };
        return {
          draftConfig: {
            ...s.draftConfig,
            solver_settings: pruneThermalParameterSweeps(thermalNext),
          },
        };
      }
      return {
        draftConfig: {
          ...s.draftConfig,
          solver_settings: next as SolverSettings,
        },
      };
    }),

  applyMeshDerivedTemplate: (template, meshMode) =>
    set((s) => {
      const current = s.draftConfig.solver_settings;
      if (current.template === template) return s;
      const draft = defaultDraftFor(template);
      if (!draft) return s;
      // Only the two CFD flow templates share a shape (`CfdFlowConfigBase`).
      // Across categories — a CHT mesh under a CFD draft — there is nothing
      // to carry, so fall back to the wholesale swap. That is still a
      // correction, and it is what the pre-ENG-2444 category-level effect
      // did; standing down instead would leave the draft on a template its
      // mesh cannot run.
      const shareShape =
        isCfdFlowSolver(current) && isCfdFlowSolver(draft as SolverSettings);
      if (!shareShape) {
        return {
          draftConfig: {
            ...s.draftConfig,
            solver_settings:
              draft.template === 'cht_steady' && meshMode === 'cht_external'
                ? {
                    ...draft,
                    regions: {
                      ...draft.regions,
                      fluid: { fluid_material: 'air_20c' },
                    },
                  }
                : draft,
          },
        };
      }
      // Keys listed rather than spread: `current` is one of the two leaves,
      // so `{...current}` would carry a leaf-only field onto the other leaf
      // — `force_coeffs` onto internal_flow, which `InternalFlowParams`
      // drops in SILENCE (no `extra`, so no 422 to notice). Typed as the
      // shared base, so a field added there is a tsc error here until it is
      // either carried or deliberately left out.
      const shared: CfdFlowConfigBase = {
        flow_type: current.flow_type,
        rho: current.rho,
        mu: current.mu,
        nu: current.nu,
        fluid_derived: current.fluid_derived,
        flow_axis: current.flow_axis,
        inlet: current.inlet,
        outlet: current.outlet,
        overrides: current.overrides,
        turbulence_intensity: current.turbulence_intensity,
        turbulent_length_scale: current.turbulent_length_scale,
      };
      return {
        draftConfig: {
          ...s.draftConfig,
          solver_settings: {
            ...draft,
            ...shared,
            // `InternalFlowConfig.inlet` is non-null (the backend has no
            // default for it), so a null carried off the external leaf has
            // to fall back to the target's own inlet rather than blanking
            // a required field.
            inlet: shared.inlet ?? (draft as CfdFlowConfig).inlet,
          } as SolverSettings,
        },
      };
    }),

  patchSolverSettings: (patch) =>
    set((s) => ({
      draftConfig: {
        ...s.draftConfig,
        solver_settings: {
          ...s.draftConfig.solver_settings,
          ...patch,
        } as SolverSettings,
      },
    })),

  setBoundaryConditions: (bcs) =>
    set((s) => ({
      draftConfig: { ...s.draftConfig, boundary_conditions: bcs },
    })),

  patchMeshSettings: (patch) =>
    set((s) => {
      // Bail when no field actually changed — keystrokes that re-emit the
      // same parsed number (arrow-key holds, blur-then-edit) would otherwise
      // re-arm the autosave debounce and re-render every `draftConfig`
      // subscriber for no reason.
      const cur = s.draftConfig.mesh_settings;
      const changed = (Object.keys(patch) as (keyof MeshSettings)[]).some(
        (k) => patch[k] !== cur[k],
      );
      if (!changed) return s;
      return {
        draftConfig: {
          ...s.draftConfig,
          mesh_settings: { ...cur, ...patch },
        },
      };
    }),

  setStudyKind: (kind) =>
    set((s) => {
      if (s.draftConfig.kind === kind) return s;
      // Keep the surrogate surface in lockstep with the kind (ENG-3135):
      // selecting surrogate turns it on with default Sobol sampling; leaving
      // it clears sampling/optimization (and un-ranges the sweep fields) so a
      // DOE/GCI study carries no stale surrogate config that would resurrect
      // the surface on reload.
      const enabled = SURROGATE_MODEL_AVAILABLE && kind === 'surrogate';
      const base =
        s.draftConfig.surrogate_enabled === enabled
          ? s.draftConfig
          : withSurrogateEnabled(s.draftConfig, enabled);
      return { draftConfig: { ...base, kind } };
    }),

  setSurrogateEnabled: (enabled) =>
    set((s) =>
      s.draftConfig.surrogate_enabled === enabled
        ? s
        : { draftConfig: withSurrogateEnabled(s.draftConfig, enabled) },
    ),

  setAutoRetry: (settings) =>
    set((s) =>
      sameAutoRetryConfig(s.draftConfig.auto_retry ?? null, settings)
        ? s
        : { draftConfig: { ...s.draftConfig, auto_retry: settings } },
    ),

  // Named `setDraftMeshAutoRetry` (not `setMeshAutoRetry`) — the slices merge
  // into one `useStore`, and toolSlice already claims `setMeshAutoRetry` for
  // the meshgen submit opt-in (ENG-1185).
  setDraftMeshAutoRetry: (settings) =>
    set((s) =>
      sameAutoRetryConfig(s.draftConfig.mesh_auto_retry ?? null, settings)
        ? s
        : { draftConfig: { ...s.draftConfig, mesh_auto_retry: settings } },
    ),

  patchSampling: (patch) =>
    set((s) => {
      const cur = s.draftConfig.sampling ?? DEFAULT_SOBOL;
      const next: Sobol = { ...cur, ...patch };
      const changed = (Object.keys(patch) as (keyof Sobol)[]).some(
        (k) => patch[k] !== cur[k],
      );
      if (!changed) return s;
      return { draftConfig: { ...s.draftConfig, sampling: next } };
    }),

  patchOptimization: (patch) =>
    set((s) => {
      if (patch === null) {
        if (s.draftConfig.optimization === null) return s;
        return { draftConfig: { ...s.draftConfig, optimization: null } };
      }
      const cur = s.draftConfig.optimization ?? DEFAULT_OPTIMIZATION;
      const next: OptimizationExtension = { ...cur, ...patch };
      const changed = (
        Object.keys(patch) as (keyof OptimizationExtension)[]
      ).some((k) => patch[k] !== cur[k]);
      if (!changed && s.draftConfig.optimization !== null) return s;
      return { draftConfig: { ...s.draftConfig, optimization: next } };
    }),

  addObjective: (objective) =>
    set((s) => {
      const cur = s.draftConfig.optimization ?? DEFAULT_OPTIMIZATION;
      return {
        draftConfig: {
          ...s.draftConfig,
          optimization: { ...cur, objectives: [...cur.objectives, objective] },
        },
      };
    }),

  removeObjective: (index) =>
    set((s) => {
      const cur = s.draftConfig.optimization;
      if (!cur) return s;
      const objectives = cur.objectives.filter((_, i) => i !== index);
      // Removing the last objective collapses to a DOE-only run; mirror
      // backend's `optimization is null` ⇔ DOE invariant.
      return {
        draftConfig: {
          ...s.draftConfig,
          optimization: objectives.length === 0 ? null : { ...cur, objectives },
        },
      };
    }),

  updateObjective: (index, patch) =>
    set((s) => {
      const cur = s.draftConfig.optimization;
      if (!cur || !cur.objectives[index]) return s;
      const target = cur.objectives[index];
      const merged = { ...target, ...patch };
      const changed = (Object.keys(patch) as (keyof Objective)[]).some(
        (k) => patch[k] !== target[k],
      );
      if (!changed) return s;
      const objectives = cur.objectives.map((o, i) =>
        i === index ? merged : o,
      );
      return {
        draftConfig: { ...s.draftConfig, optimization: { ...cur, objectives } },
      };
    }),

  setMeshPicks: (ids) =>
    set((s) => ({ draftConfig: { ...s.draftConfig, meshPicks: ids } })),

  upsertManualDesignPoint: (dp) =>
    set((s) => {
      const current = s.draftConfig.extra_design_points;
      const idx = current.findIndex((e) => e.id === dp.id);
      const next =
        idx === -1
          ? [...current, dp]
          : current.map((e, i) => (i === idx ? dp : e));
      return { draftConfig: { ...s.draftConfig, extra_design_points: next } };
    }),

  removeManualDesignPoint: (id) =>
    set((s) => ({
      draftConfig: {
        ...s.draftConfig,
        extra_design_points: s.draftConfig.extra_design_points.filter(
          (e) => e.id !== id,
        ),
      },
      // If the row being removed is the one under edit, exit edit mode.
      editingManualDpId:
        s.editingManualDpId === id ? null : s.editingManualDpId,
    })),

  pruneStaleManualDesignPoints: (validInputIds) => {
    const validSet = new Set(validInputIds);
    const before = get().draftConfig.extra_design_points;
    const dropped = before.filter((e) => !validSet.has(e.input_id));
    if (dropped.length === 0) return [];
    const droppedIds = dropped.map((e) => e.id);
    const droppedSet = new Set(droppedIds);
    set((s) => ({
      draftConfig: {
        ...s.draftConfig,
        extra_design_points: s.draftConfig.extra_design_points.filter(
          (e) => !droppedSet.has(e.id),
        ),
      },
      editingManualDpId:
        s.editingManualDpId != null && droppedSet.has(s.editingManualDpId)
          ? null
          : s.editingManualDpId,
      selectedManualDpIds: new Set(
        [...s.selectedManualDpIds].filter((id) => !droppedSet.has(id)),
      ),
    }));
    return droppedIds;
  },

  editingManualDpId: null,
  setEditingManualDpId: (id) => set({ editingManualDpId: id }),
  manualDpInputOptions: null,
  setManualDpInputOptions: (value) => set({ manualDpInputOptions: value }),

  selectedManualDpIds: new Set<string>(),
  setSelectedManualDpIds: (ids) => set({ selectedManualDpIds: new Set(ids) }),
  clearSelectedManualDpIds: () => set({ selectedManualDpIds: new Set() }),
  toggleSelectedManualDpId: (id) =>
    set((s) => {
      const next = new Set(s.selectedManualDpIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedManualDpIds: next };
    }),
});
